// CommandPaletteProvider / useCommandPalette (RFC-0011 Task 2.1).
//
// Shared open/close state for the global Cmd-K / Ctrl-K command palette, mirroring
// the shape of context/connect-repo.tsx (createContext + { isOpen, open, close },
// safe no-op default outside a provider, useCallback/useMemo).
//
// This provider lives INSIDE the router (mounted in PortalLayout, Task 2.3) so it
// can read the active route via useLocation/matchPath. When the palette is opened
// from a `/projects/:repo/*` route, the initial search scope defaults to that repo
// (PD-0017 P5); otherwise it defaults to all projects. The resolved scope is
// exposed to consumers (the CommandPalette overlay seeds its local scope from it).
//
// Global shortcut: one window `keydown` listener toggles the palette on Meta+K
// (macOS) / Control+K (Win/Linux) with preventDefault. First-modal-wins (v1, no
// overlay-stack manager — RFC Open Question): while the connect-repo modal is
// open (useConnectRepo().isOpen), the shortcut is a no-op.
//
// Note: exporting both the provider and the hook from one file triggers the same
// accepted `react-refresh/only-export-components` lint *warning* as connect-repo.tsx
// / theme.tsx — lint still passes; this mirrors the repo pattern.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useConnectRepo } from '@/context/connect-repo';

export type SearchScope = 'repo' | 'all';

interface CommandPaletteContextValue {
  /** Whether the command palette is currently open. */
  isOpen: boolean;
  /** Open the palette, resolving the initial scope from the current route. */
  open: () => void;
  /** Close the palette. */
  close: () => void;
  /** Scope resolved from the route at the moment the palette was opened. */
  initialScope: SearchScope;
  /**
   * Repo SHORT name in context when the palette was opened on `/projects/:repo/*`,
   * else `undefined` (all-projects context).
   */
  repoName?: string;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/** Resolve the repo short name from a pathname, or `undefined` when not in a repo. */
function repoFromPathname(pathname: string): string | undefined {
  // `/projects/:repo/*` also matches the bare `/projects/:repo` (empty splat).
  const match =
    matchPath('/projects/:repo/*', pathname) ?? matchPath('/projects/:repo', pathname);
  const repo = match?.params.repo;
  return repo ? decodeURIComponent(repo) : undefined;
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isOpen: connectRepoOpen } = useConnectRepo();

  const [isOpen, setIsOpen] = useState(false);
  const [initialScope, setInitialScope] = useState<SearchScope>('all');
  const [repoName, setRepoName] = useState<string | undefined>(undefined);

  const open = useCallback(() => {
    const repo = repoFromPathname(location.pathname);
    setRepoName(repo);
    setInitialScope(repo ? 'repo' : 'all');
    setIsOpen(true);
  }, [location.pathname]);

  const close = useCallback(() => setIsOpen(false), []);

  // One global shortcut listener: Meta+K (macOS) / Ctrl+K (Win/Linux) toggles the
  // palette. First-modal-wins — a no-op while the connect-repo modal is open.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isK = event.key === 'k' || event.key === 'K';
      if (!isK || !(event.metaKey || event.ctrlKey)) return;
      if (connectRepoOpen) return; // first-modal-wins (v1)
      event.preventDefault();
      if (isOpen) close();
      else open();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, connectRepoOpen, open, close]);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ isOpen, open, close, initialScope, repoName }),
    [isOpen, open, close, initialScope, repoName],
  );

  return (
    <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
  );
}

/**
 * Access the command-palette controls. Returns safe no-op defaults when called
 * outside a provider (never throws), matching connect-repo.tsx's posture.
 */
export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (ctx) return ctx;
  return { isOpen: false, open: () => {}, close: () => {}, initialScope: 'all', repoName: undefined };
}
